import React, { useMemo, useEffect } from "react";
import { Select } from "@mantine/core";
import { useLookupDictionaries } from "@/hooks/useLookupDictionaries";
import { LoadingIndicator } from "@/components/generic";
import { LookupDictionary } from "@/utils/api";
import { useApp } from "@/contexts";
import { STATUSES } from "@sdflc/utils";

export interface LookupDictionarySelectorProps {
  /** Selected lookup dictionary ID */
  value: string | null | undefined;

  /** Callback when selection changes */
  onChange?: (
    lookupDictionaryId: string | null,
    lookupDictionary?: LookupDictionary
  ) => void;

  /** Placeholder text */
  placeholder?: string;

  /** Whether to include inactive dictionaries */
  includeInactive?: boolean;

  /** Whether to show only external (URL-based) dictionaries */
  externalOnly?: boolean;

  /** Whether to show only local dictionaries */
  localOnly?: boolean;

  /** Custom filter function */
  dictionaryFilter?: (lookupDictionary: LookupDictionary) => boolean;

  /** Whether the field is required */
  required?: boolean;

  /** Field label */
  label?: string;

  /** Loading placeholder text */
  loadingPlaceholder?: string;

  /** Empty state text */
  emptyText?: string;

  /** Error message */
  error?: React.ReactNode;

  /** Whether the field is disabled */
  disabled?: boolean;

  /** Component size */
  size?: "xs" | "sm" | "md" | "lg" | "xl";

  /** Additional props passed to Select */
  [key: string]: any;
}

export const LookupDictionarySelector: React.FC<
  LookupDictionarySelectorProps
> = ({
  value,
  onChange,
  placeholder = "Select a dictionary",
  includeInactive = false,
  externalOnly = false,
  localOnly = false,
  dictionaryFilter,
  required = false,
  label,
  loadingPlaceholder = "Loading dictionaries...",
  emptyText = "No dictionaries available",
  error,
  disabled,
  size = "md",
  ...selectProps
}) => {
  const { user } = useApp();
  const { lookupDictionaries, loading, isEmpty, lastError } =
    useLookupDictionaries({
      autoLoad: true,
      userId: user?.id,
      initialFilters: includeInactive ? {} : { status: [STATUSES.ACTIVE] },
    });

  // Filter and prepare options
  const dictionaryOptions = useMemo(() => {
    let filteredDictionaries: any[] = lookupDictionaries;

    // Filter by external/local if specified
    if (externalOnly) {
      filteredDictionaries = filteredDictionaries.filter(
        (dict) => !!dict.externalUrl
      );
    } else if (localOnly) {
      filteredDictionaries = filteredDictionaries.filter(
        (dict) => !dict.externalUrl
      );
    }

    // Apply custom filter if provided
    if (dictionaryFilter) {
      filteredDictionaries = filteredDictionaries.filter(dictionaryFilter);
    }

    // Convert to select options
    return filteredDictionaries.map((dictionary) => {
      const typeLabel = dictionary.externalUrl ? " (external)" : " (local)";
      const description = dictionary.description
        ? ` - ${dictionary.description}`
        : "";

      return {
        value: dictionary.id || "",
        label: `${dictionary.name}${typeLabel}${description}`,
        disabled: dictionary.status !== STATUSES.ACTIVE,
      };
    });
  }, [lookupDictionaries, externalOnly, localOnly, dictionaryFilter]);

  // Create a map for quick lookup by ID
  const dictionaryMap = useMemo(() => {
    const map = new Map<string, LookupDictionary>();
    lookupDictionaries.forEach((dictionary) => {
      if (dictionary.id) {
        map.set(dictionary.id, dictionary);
      }
    });
    return map;
  }, [lookupDictionaries]);

  // Auto-select first dictionary if required and no value provided
  useEffect(() => {
    if (
      required &&
      !value &&
      dictionaryOptions.length > 0 &&
      !loading &&
      onChange
    ) {
      const firstOption = dictionaryOptions.find((opt) => !opt.disabled);
      if (firstOption) {
        const dictionaryObject = dictionaryMap.get(firstOption.value);
        onChange(firstOption.value, dictionaryObject);
      }
    }
  }, [required, value, dictionaryOptions, loading, onChange, dictionaryMap]);

  // Handle onChange with object lookup
  const handleChange = (selectedValue: string | null) => {
    if (selectedValue) {
      const selectedDictionary = dictionaryMap.get(selectedValue);
      onChange?.(selectedValue, selectedDictionary);
    } else {
      onChange?.(null, undefined);
    }
  };

  // Determine select state
  const isInitialLoading = loading && lookupDictionaries.length === 0;
  const hasError = lastError && !loading;
  const isEmptyState = isEmpty && !loading;

  const selectState = {
    data: isInitialLoading || hasError || isEmptyState ? [] : dictionaryOptions,
    placeholder: isInitialLoading
      ? loadingPlaceholder
      : hasError
      ? "Error loading dictionaries"
      : isEmptyState
      ? emptyText
      : placeholder,
    disabled: disabled || isInitialLoading || hasError || isEmptyState,
    error: hasError ? error || "Failed to load dictionaries" : error,
    rightSection:
      isInitialLoading || (loading && lookupDictionaries.length > 0) ? (
        <LoadingIndicator size={size} color="gray" />
      ) : (
        selectProps.rightSection
      ),
  };

  return (
    <Select
      {...selectProps}
      label={label}
      size={size}
      data={selectState.data}
      value={value ?? null}
      onChange={handleChange}
      placeholder={selectState.placeholder}
      clearable={!required}
      searchable
      required={required}
      disabled={selectState.disabled}
      error={selectState.error}
      rightSection={selectState.rightSection}
    />
  );
};
